import {
    type IPublicClientApplication,
    type Configuration,
    type PerformanceTelemetryReporterConfiguration,
    type BrowserAuthOptions,
    type CacheOptions,
    type BrowserSystemOptions,
    PublicClientApplication,
    createNestablePublicClientApplication,
    createStandardPublicClientApplication,
    LogLevel,
    BrowserCacheLocation,
    ProtocolMode,
    version,
} from '@azure/msal-browser-1p';
import { isBootFeatureEnabled } from 'owa-metatags/lib/isBootFeatureEnabled';
import { getClientVersion, getHostHub, isHosted, isHostedCalendar, TEAMS_HUB } from 'owa-config';
import { getOrigin } from 'owa-url/lib/getOrigin';
import { trace } from 'owa-trace';
import { getCommonAuthorityUrlFromMetaTag } from './utils/getAuthority';
import { OwaApplicationId } from 'owa-service/lib/constants/ApplicationId';
import { isBusinessDomain } from './isMsalEnabledForBusiness';
import { isConsumerDomain } from './isMsalEnabledForConsumerDomain';
import { logStartCoreUsage } from 'owa-analytics-start';
import { INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT } from 'owa-analytics-types';

let msalInstancePromise: Promise<IPublicClientApplication> | null = null;
let appSpecificConfiguration: AppSpecificConfiguration | undefined = undefined;

//Use the msal version property to determine if we're running v5 or later.
export function isMsalV5(): boolean {
    return parseInt(version.split('.')[0], 10) >= 5;
}

interface CustomGlobalThis {
    __JSWRAPPER?: boolean; // This is a flag set by MCAS
}

declare var globalThis: CustomGlobalThis;

export function getMsalInstance(): Promise<IPublicClientApplication> {
    // NOTE: getMsalInstance will only work after a call (during boot) to createMsalInstance which
    // configures MSAL with the correct clientId. If someone tries to use MSAL before creation, then throw
    // this error to let the developer know that MSAL is being called too early, or the boot flight is disabled.
    return msalInstancePromise ?? Promise.reject(new Error('MSAL instance not available'));
}

export type AppSpecificConfiguration = {
    auth: Pick<BrowserAuthOptions, 'clientId' | 'redirectUri'>;
    cache?: Pick<CacheOptions, 'cacheLocation'>;
    protocolMode?: ProtocolMode;

    /**
     * Set to true to indicate that this app should NOT fallback to other host token mechanisms
     * (ie. Hub SDK) if NAA is unsupported in the host. Used if you require use of your clientId
     * and don't have an existing security-approved/working solution that relies on full trust auth
     */
    disableGetAuthTokenFallback?: boolean;

    /**
     * If set, indicates the URI scheme fragment to use when constructing the broker redirect URI.
     */
    nestedAppAuthScheme?: string;

    /**
     * Opt in to invoke acquireTokenPopup when the app is hosted and receives an interaction required error,
     * to account for scenarios where the host doesn't support sign-in banner.
     * Currently only used by hosted Calendar in Teams */
    enableTokenPopUpWhenHosted?: boolean;
};

// To do: Need to modify this function to return config for different environments.
export function createMsalInstance(appConfig?: AppSpecificConfiguration): void {
    if (msalInstancePromise) {
        // NOTE: this function should only be called once during boot with a specific clientId. To prevent
        // other callers from calling init with potentially different parameters, and to retain control
        // of when MSAL init occurs and use of the correct clientId, throw an error here. This should not
        // be hit during normal runtime and execution, and is to let client developers know.
        throw new Error('MSAL instance creation is managed by boot code');
    }

    appSpecificConfiguration = appConfig;

    const useNestedAppAuth = isHosted();

    const protocolMode =
        appConfig?.protocolMode ??
        (isTdsOidcAuthority(getCommonAuthorityUrlFromMetaTag())
            ? ProtocolMode.OIDC
            : OwaApplicationId == (appConfig?.auth.clientId ?? OwaApplicationId) &&
              !useNestedAppAuth && // Only enable EAR for non-hosted scenarios
              ((isBootFeatureEnabled('auth-msaljs-enableEAR-enterprise') && isBusinessDomain()) ||
                  (isBootFeatureEnabled('auth-msaljs-enableEAR-consumer') && isConsumerDomain()))
            ? ProtocolMode.EAR
            : ProtocolMode.AAD);

    const msalConfig: Configuration = {
        auth: {
            clientId: OwaApplicationId,
            redirectUri: `${getOrigin()}/mail/`,
            authority: getCommonAuthorityUrlFromMetaTag(),
            navigateToLoginRequestUrl: true, // v4 compat; v5 ignores this and uses handleRedirectPromise options
            clientCapabilities: ['CP1'],
            ...appSpecificConfiguration?.auth,
            supportsNestedAppAuth: useNestedAppAuth, // v4 compat; v5 ignores this
            protocolMode, // v4 compat; v5 reads from system
        } as Configuration['auth'],
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            ...appSpecificConfiguration?.cache,
        },
        system: {
            loggerOptions: getLoggerOptions(),
            allowPlatformBroker: false,
            allowRedirectInIframe: true,
            protocolMode, // v5; v4 ignores this and reads from auth
        } as Configuration['system'],
        ...(isMsalV5() &&
            isBootFeatureEnabled('auth-msaljs-iframeTimeoutTelemetry') && {
                experimental: {
                    iframeTimeoutTelemetry: true,
                },
            }),
    };

    // Add knownAuthorities for TDS OIDC server to bypass authority validation
    if (isTdsOidcAuthority(msalConfig.auth.authority)) {
        const authorityUrl = new URL(msalConfig.auth.authority ?? '');
        msalConfig.auth.knownAuthorities = [authorityUrl.host];
    }

    // When MCAS session starts, MCAS puts __JSWRAPPER into the window global var. If this is present, we should disable CAE capabilities.
    if (globalThis.__JSWRAPPER || isBootFeatureEnabled('auth-msaljs-disableCaeCapabilities')) {
        msalConfig.auth.clientCapabilities = undefined;
    }

    // Configure Telemetry for MATS integration
    const telemConfig: PerformanceTelemetryReporterConfiguration = {
        appName: 'OWA',
        appVersion: getClientVersion(),
    };

    const createStandard = isMsalV5()
        ? createStandardPublicClientApplication
        : PublicClientApplication.createPublicClientApplication.bind(PublicClientApplication);

    // Diagnostic telemetry: in hosted Calendar inside Teams hub, capture the NAA bridge
    // pre-conditions so we can attribute MsalAcquireToken `no_tokens_found` failures to
    // (a) wrong useNestedAppAuth routing, (b) polyfill skipped due to nested iframe,
    // (c) polyfill skipped because Teams' runtimeConfig doesn't advertise nestedAppAuth,
    // or (d) bridge present but BridgeProxy handshake failed.
    if (isHostedCalendar() && getHostHub() === TEAMS_HUB) {
        logStartCoreUsage(
            'MsalInitContext',
            /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! - Boot-time diagnostic that fires before
             * the account source list store is reliably initialized (NAA path defers
             * account init until after MSAL is ready). No MailboxInfo is available. */
            INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
            {
                useNestedAppAuth,
                bridgePresent:
                    typeof (
                        self as unknown as {
                            nestedAppAuthBridge?: unknown;
                        }
                    ).nestedAppAuthBridge !== 'undefined',
                isNested: self.parent !== self.top,
            }
        );
    }

    msalInstancePromise = useNestedAppAuth
        ? createNestablePublicClientApplication(msalConfig)
        : createStandard(msalConfig, telemConfig);

    // purposefully don't await the promise, as any consumers will await getMsalInstance when
    // they'll first need it, so no need to artificially do it ahead of time
}

// Get the client id of the application creating MSAL instance.
export function getClientId(): string {
    return appSpecificConfiguration?.auth.clientId ?? OwaApplicationId;
}

//Get the redirect uri where authentication responses can be received by the app.
export function getRedirectUri(): string {
    return appSpecificConfiguration?.auth.redirectUri ?? `${getOrigin()}/mail/`;
}

// Get the protocol mode used by the application.
export function getProtocolMode(): ProtocolMode | undefined {
    return appSpecificConfiguration?.protocolMode ?? undefined;
}

export function getNestedAppAuthScheme(): string | undefined {
    return appSpecificConfiguration?.nestedAppAuthScheme;
}

export function getEnableTokenPopUpWhenHosted(): boolean {
    return appSpecificConfiguration?.enableTokenPopUpWhenHosted ?? false;
}

function getLoggerOptions(): BrowserSystemOptions['loggerOptions'] | undefined {
    if (isBootFeatureEnabled('auth-msaljs-EnableLogging')) {
        return {
            logLevel: LogLevel.Trace,
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        trace.warn(message);
                        return;
                    case LogLevel.Info:
                        trace.info(message);
                        return;
                    case LogLevel.Verbose:
                        trace.verbose(message);
                        return;
                    case LogLevel.Warning:
                        trace.warn(message);
                        return;
                    default:
                        trace.info(message);
                        return;
                }
            },
        };
    }
    return undefined;
}

function isTdsOidcAuthority(authority: string | undefined): boolean {
    if (!authority) {
        return false;
    }
    try {
        const url = new URL(authority);
        return url.pathname.startsWith('/owa/tds/openid');
    } catch {
        return false;
    }
}
